import React, { useState } from "react";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Alert, Button, Input, message, Modal, Space, Typography } from "antd";
import { useAuthenticatedFetch } from "@/Api/Axios";

const { Text } = Typography;

// Days after which the account data is permanently removed.
export const DATA_RETENTION_DAYS = 7;

/**
 * Confirmation modal for self service account deletion.
 * Runs the same soft delete flow the owner triggers from the admin panel.
 */
export default function DeleteAccountModal({ open, onClose }) {
  const fetch = useAuthenticatedFetch();
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const close = () => {
    setConfirmText("");
    onClose();
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const response = await fetch.delete("user/account");
      if (response?.success !== false) {
        message.success(
          response?.message ||
            `Your account has been deleted. All of your data will be permanently removed within ${DATA_RETENTION_DAYS} days.`
        );
        localStorage.removeItem("authorization");
        localStorage.removeItem("userId");
        sessionStorage.removeItem("adminResumeAccessToken");
        close();
        window.location.href = "/app/login";
      } else {
        message.error(response?.message || "Failed to delete account");
      }
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to delete account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <span style={{ color: "#cf1322" }}>
          <ExclamationCircleOutlined style={{ marginRight: "8px" }} />
          Delete your account?
        </span>
      }
      open={open}
      onCancel={close}
      footer={null}
      centered
    >
      <Alert
        type="warning"
        showIcon
        message={`All your data will be permanently removed within ${DATA_RETENTION_DAYS} days`}
        description={
          <span>
            Your account is closed immediately and you lose access to everything below. After{" "}
            {DATA_RETENTION_DAYS} days it is deleted for good and cannot be recovered.
          </span>
        }
        style={{ marginBottom: "16px" }}
      />

      <ul style={{ color: "#4b5563", fontSize: "14px", paddingLeft: "20px", marginBottom: "16px" }}>
        <li>All resumes and cover letters</li>
        <li>Your public resume links stop working</li>
        <li>Remaining credits and purchase history</li>
        <li>Your profile and login access</li>
      </ul>

      <Text style={{ fontSize: "14px", color: "#4b5563" }}>
        Type <Text strong>DELETE</Text> to confirm:
      </Text>
      <Input
        size="large"
        value={confirmText}
        onChange={(e) => setConfirmText(e.target.value)}
        placeholder="DELETE"
        style={{ marginTop: "8px" }}
      />

      <Space style={{ width: "100%", justifyContent: "flex-end", marginTop: "24px" }}>
        <Button onClick={close}>Cancel</Button>
        <Button
          danger
          type="primary"
          loading={loading}
          disabled={confirmText.trim().toUpperCase() !== "DELETE"}
          onClick={handleDelete}
        >
          Delete My Account
        </Button>
      </Space>
    </Modal>
  );
}
